import { createSelector } from "@reduxjs/toolkit";
import { IMaconomyProject, IProjectPns } from "@schemas/projectSchema";
import { openapi } from "../../openapi";
import { selectCurrentProjectId } from "../global/globalSliceCommon";
import { MULTICONSULT_MAL_PROJECT_ID } from "../mc-shared/sharedConstants";
import { IRootState } from "../store";
import { projectRTK } from "./projectRTK";

export type IMergedMaconomyProject = IMaconomyProject & {
  fromKafka: boolean;
  fromMcapps: boolean;
};

export const selectHttpGetSingleProject = createSelector(
  [selectCurrentProjectId, (state) => state],
  (projectId, state) => {
    if (!projectId) return null;
    return projectRTK.endpoints.getProject.select(projectId)(state).data || null;
  },
);

const selectProjectNoFromDepricated = (s: IRootState) => s.adminReducer.project?.maconomy?.projectNo || null;
export const selectProjectNo = createSelector(
  [selectHttpGetSingleProject, selectProjectNoFromDepricated],
  (project, deprecatedProjectNo) => {
    if (project?.maconomy?.projectNo != null) return project.maconomy.projectNo;
    return deprecatedProjectNo;
  },
);

export const selectProjectPns = createSelector(
  [selectHttpGetSingleProject],
  (project): IProjectPns => project?.pns ?? [],
);

export const selectIsTemplateProject = createSelector([selectCurrentProjectId], (projectId) => {
  if (projectId === MULTICONSULT_MAL_PROJECT_ID) return true;
  return false;
});

export const selectHttpProjectMembers = createSelector(
  [selectCurrentProjectId, (state) => state],
  (projectId, state) => {
    if (!projectId) return [];
    return openapi.endpoints.getProjectMembers.select({ projectId })(state).data || [];
  },
);

export const selectMergedProjectList = createSelector([(state) => state], (state) => {
  const mcappsProjects = openapi.endpoints.getProjectsMcapps.select()(state).data;
  const maconomyProjects = openapi.endpoints.getProjectsMaconomy.select()(state).data;

  const _p: IMergedMaconomyProject[] =
    mcappsProjects?.map((p) => ({
      ...p.maconomy,
      fromKafka: false,
      fromMcapps: true,
    })) || [];
  const _m: IMergedMaconomyProject[] =
    maconomyProjects?.map((p) => ({
      ...p,
      fromKafka: true,
      fromMcapps: false,
    })) || [];

  const mergedProjectMap = new Map<string, IMergedMaconomyProject>();

  for (const project of _m) {
    mergedProjectMap.set(project.projectNo.toString(), project);
  }

  for (const project of _p) {
    const existingProject = mergedProjectMap.get(project.projectNo.toString());

    mergedProjectMap.set(project.projectNo.toString(), {
      ...project,
      fromKafka: existingProject?.fromKafka === true,
      fromMcapps: true,
    });
  }

  const mergedProjects: IMergedMaconomyProject[] = Array.from(mergedProjectMap.values());

  return mergedProjects.sort((a, b) => a.name.localeCompare(b.name));
});
